import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { supabase } from "../lib/supabase";
import { useOrganization } from "../organization/OrganizationProvider";

export function AcceptInvitationPage() {
  const { user, loading } = useAuth();
  const { refreshOrganizations } = useOrganization();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token")?.trim() ?? "";

  const attempted = useRef(false);
  const [accepting, setAccepting] = useState(false);
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const currentPath = token
    ? `/accept-invitation?token=${encodeURIComponent(token)}`
    : "/accept-invitation";
  const loginPath = `/login?returnTo=${encodeURIComponent(currentPath)}`;

  useEffect(() => {
    if (loading || !user || !token || attempted.current) return;

    attempted.current = true;
    setAccepting(true);
    setError(null);

    void (async () => {
      const { data, error: rpcError } = await supabase.rpc(
        "accept_organization_invitation",
        { p_token: token },
      );

      if (rpcError) {
        setError(rpcError.message);
        setAccepting(false);
        return;
      }

      setOrganizationId(String(data));
      await refreshOrganizations();
      setAccepting(false);
    })();
  }, [loading, user, token, refreshOrganizations]);

  if (loading) {
    return (
      <main className="center-screen">
        <div className="loading-card">Validando sesión…</div>
      </main>
    );
  }

  if (!token) {
    return (
      <main className="invitation-page">
        <section className="invitation-panel">
          <span className="eyebrow">Invitación</span>
          <h1>Enlace inválido</h1>
          <p>El enlace no contiene un token de invitación válido.</p>
          <Link className="primary-link-button" to="/">
            Ir al inicio
          </Link>
        </section>
      </main>
    );
  }

  if (!user) {
    return (
      <main className="invitation-page">
        <section className="invitation-panel">
          <span className="eyebrow">Invitación</span>
          <h1>Inicia sesión para aceptar</h1>
          <p>
            Debes ingresar con el mismo correo al que fue enviada la invitación.
          </p>
          <Link className="primary-link-button" to={loginPath}>
            Iniciar sesión
          </Link>
        </section>
      </main>
    );
  }

  if (accepting) {
    return (
      <main className="invitation-page">
        <section className="invitation-panel">
          <span className="eyebrow">Invitación</span>
          <h1>Activando acceso…</h1>
          <p>Estamos vinculando tu cuenta con la empresa.</p>
        </section>
      </main>
    );
  }

  if (error) {
    return (
      <main className="invitation-page">
        <section className="invitation-panel">
          <span className="eyebrow">Invitación</span>
          <h1>No se pudo aceptar</h1>
          <div className="form-error">{error}</div>
          <p>
            Verifica que hayas iniciado sesión con el correo correcto y que la
            invitación continúe vigente.
          </p>
          <Link className="primary-link-button" to="/">
            Volver al panel
          </Link>
        </section>
      </main>
    );
  }

  if (organizationId) {
    return (
      <main className="invitation-page">
        <section className="invitation-panel">
          <span className="eyebrow">Acceso activado</span>
          <h1>Invitación aceptada</h1>
          <p>
            Tu cuenta ya pertenece a la empresa. Puedes seleccionarla desde el
            panel multiempresa.
          </p>
          <Link className="primary-link-button" to="/">
            Entrar al panel
          </Link>
        </section>
      </main>
    );
  }

  return (
    <main className="invitation-page">
      <section className="invitation-panel">
        <span className="eyebrow">Invitación</span>
        <h1>Preparando acceso…</h1>
      </section>
    </main>
  );
}
